/**
 * CalendarSyncDialog — Module 12 personal calendar sync UI.
 *
 * Sits inside My Guru → Profile & Preferences. Shows:
 *   • MY GLEAM GURU SCHEDULE — the personal live feed for the signed-in user.
 *   • COMPANY SCHEDULE — visible only if backend reports can_manage_company.
 *
 * Every action is server-side. This component NEVER derives or guesses
 * a user's feed URL — it always uses what `/api/account/calendar-sync/status`
 * returned. That's the spec §11-22 guarantee.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Calendar as CalendarIcon, Apple, Chrome, Link2,
  RefreshCcw, CheckCircle2, Copy, Loader2, ShieldCheck, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Copy helper (uses async Clipboard API, falls back to textarea trick)
// ---------------------------------------------------------------------------
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
};

// ===========================================================================
// InstructionsPanel — Apple / Google step-by-step (nested dialog)
// ===========================================================================
const InstructionsPanel = ({ open, onClose, platform, feedUrl, webcalUrl, onCopy }) => {
  if (!platform) return null;

  const steps = platform === 'apple'
    ? [
        { text: 'Copy the calendar link below.' },
        { text: 'On iPhone or iPad, open Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar. On Mac, open Calendar → File → New Calendar Subscription.' },
        { text: 'Paste the link, tap Next, then Save.' },
        { text: 'Your Gleam Guru schedule appears in Apple Calendar.' },
      ]
    : [
        { text: 'Copy the calendar link below.' },
        { text: 'On a computer, open Google Calendar → click the “+” next to “Other calendars” → From URL.' },
        { text: 'Paste the link and click Add calendar.' },
        { text: 'Your Gleam Guru schedule appears in Google Calendar. Mobile refreshes automatically.' },
      ];

  const platformLabel = platform === 'apple' ? 'Apple Calendar' : 'Google Calendar';
  const Icon = platform === 'apple' ? Apple : Chrome;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="calendar-instructions-dialog">
        <div className="bg-zinc-950 px-6 pt-5 pb-4 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F56F05] flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Subscribe with</p>
              <p className="text-lg font-black tracking-tight">{platformLabel}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <DialogHeader className="text-left space-y-1 mb-3">
            <DialogTitle className="text-base">Add your Gleam Guru schedule</DialogTitle>
            <DialogDescription className="text-[12px]">
              Follow these steps once. The subscription refreshes automatically.
            </DialogDescription>
          </DialogHeader>

          <ol className="space-y-2.5 mb-4" data-testid="calendar-instructions-steps">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#F56F05] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <span className="flex-1 pt-0.5 text-[13px] text-zinc-700 leading-relaxed">{s.text}</span>
              </li>
            ))}
          </ol>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">Calendar link</p>
            <div className="flex items-start gap-2">
              <code
                className="flex-1 text-[11px] text-zinc-800 break-all leading-snug font-mono"
                data-testid="instructions-feed-url"
              >
                {feedUrl}
              </code>
              <Button
                size="sm"
                onClick={() => onCopy(feedUrl)}
                className="bg-[#F56F05] hover:bg-orange-600 h-8 px-3 flex-shrink-0"
                data-testid="instructions-copy-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            {platform === 'apple' && webcalUrl && (
              <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                On iPhone or iPad, you can also tap this{' '}
                <a
                  href={webcalUrl}
                  className="text-[#F56F05] font-semibold underline"
                  data-testid="instructions-webcal-link"
                >
                  one-tap subscribe link
                </a>{' '}
                to open the Calendar sheet directly.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 pt-0">
          <Button variant="outline" onClick={onClose} className="w-full" data-testid="instructions-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
// SubscriptionCard — one row for a single scope (personal or company).
// ===========================================================================
const SubscriptionCard = ({
  scopeKey, title, subtitle, badge, feed, headers, onChanged,
}) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [instructions, setInstructions] = useState(null); // 'apple' | 'google' | null

  const ready = !!feed;
  const feedUrl = feed?.feed_url || '';
  const webcalUrl = feed?.webcal_url || '';

  const doEnable = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/account/calendar-sync/${scopeKey}/enable`, {}, { headers });
      await onChanged();
      toast.success('Calendar ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable calendar sync');
    } finally { setBusy(false); }
  };

  const doRegenerate = async () => {
    setBusy(true); setConfirming(false);
    try {
      await axios.post(`${API}/api/account/calendar-sync/${scopeKey}/regenerate`, {}, { headers });
      await onChanged();
      toast.success('Calendar link regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate');
    } finally { setBusy(false); }
  };

  const doCopy = async (url) => {
    const ok = await copyToClipboard(url || feedUrl);
    if (ok) toast.success('Calendar link copied');
    else toast.error('Could not copy — long-press the link to copy manually');
  };

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid={`calendar-sync-card-${scopeKey}`}>
      <CardContent className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[15px] font-bold text-zinc-900">{title}</p>
              {badge && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold tracking-widest rounded border bg-orange-50 text-[#F56F05] border-orange-200">
                  <ShieldCheck className="w-3 h-3" /> {badge}
                </span>
              )}
            </div>
            <p className="text-[12px] text-zinc-500 mt-0.5">{subtitle}</p>
          </div>
          {ready && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0"
              data-testid={`calendar-sync-ready-${scopeKey}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Calendar Ready
            </span>
          )}
        </div>

        {!ready ? (
          <div className="mt-4">
            <Button
              onClick={doEnable}
              disabled={busy}
              className="w-full sm:w-auto bg-[#F56F05] hover:bg-orange-600"
              data-testid={`calendar-sync-enable-${scopeKey}`}
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              <CalendarIcon className="w-4 h-4 mr-1.5" /> Turn on calendar sync
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`calendar-sync-actions-${scopeKey}`}>
              <PlatformButton
                icon={Apple}
                label="Apple Calendar"
                hint="iPhone, iPad, Mac"
                onClick={() => setInstructions('apple')}
                testId={`calendar-sync-apple-${scopeKey}`}
              />
              <PlatformButton
                icon={Chrome}
                label="Google Calendar"
                hint="Google Calendar"
                onClick={() => setInstructions('google')}
                testId={`calendar-sync-google-${scopeKey}`}
              />
              <PlatformButton
                icon={Link2}
                label="Copy Calendar Link"
                hint="Any compatible app"
                onClick={() => doCopy(feedUrl)}
                testId={`calendar-sync-copy-${scopeKey}`}
              />
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-zinc-100">
              <p className="text-[11px] text-zinc-500">
                Your schedule is available through your secure private feed.
              </p>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-[11px] font-semibold text-zinc-600 hover:text-red-600 underline underline-offset-2 flex items-center gap-1 flex-shrink-0"
                data-testid={`calendar-sync-regenerate-${scopeKey}`}
              >
                <RefreshCcw className="w-3 h-3" /> Regenerate link
              </button>
            </div>
          </>
        )}
      </CardContent>

      {/* Regen confirmation */}
      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent className="max-w-md" data-testid={`calendar-sync-regen-confirm-${scopeKey}`}>
          <DialogHeader>
            <DialogTitle>Regenerate calendar link?</DialogTitle>
            <DialogDescription>
              Your existing calendar subscription link will stop working. You&apos;ll need to reconnect your calendar using the new link.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button onClick={doRegenerate} disabled={busy} className="bg-[#F56F05] hover:bg-orange-600" data-testid={`calendar-sync-regen-confirm-btn-${scopeKey}`}>
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstructionsPanel
        open={!!instructions}
        onClose={() => setInstructions(null)}
        platform={instructions}
        feedUrl={feedUrl}
        webcalUrl={webcalUrl}
        onCopy={doCopy}
      />
    </Card>
  );
};

const PlatformButton = ({ icon: Icon, label, hint, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-center gap-3 rounded-lg border border-zinc-200 hover:border-[#F56F05] hover:shadow-sm bg-white p-3 text-left transition-colors"
    data-testid={testId}
  >
    <div className="w-9 h-9 rounded-lg bg-zinc-950 group-hover:bg-[#F56F05] flex items-center justify-center flex-shrink-0 transition-colors">
      <Icon className="w-4 h-4 text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-bold text-zinc-900 truncate">{label}</p>
      <p className="text-[11px] text-zinc-500 truncate">{hint}</p>
    </div>
  </button>
);

// ===========================================================================
// Main dialog
// ===========================================================================
const CalendarSyncDialog = ({ open, onClose, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/account/calendar-sync/status`, { headers });
      setState(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendar sync');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="calendar-sync-dialog">
        <div className="bg-zinc-950 px-6 pt-5 pb-4 text-white flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#F56F05] flex items-center justify-center flex-shrink-0">
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Calendar Sync</p>
              <p className="text-lg font-black tracking-tight leading-tight">Keep your work schedule in sync</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-white flex-shrink-0"
            aria-label="Close"
            data-testid="calendar-sync-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <p className="text-[13px] text-zinc-600 leading-relaxed">
            Keep your Gleam Guru work schedule connected to the calendar you use every day.
          </p>

          {loading || !state ? (
            <div className="py-10 text-center text-zinc-400">
              <Loader2 className="w-5 h-5 animate-spin inline mr-1.5" /> Loading…
            </div>
          ) : (
            <>
              <SubscriptionCard
                scopeKey="personal"
                title="My Gleam Guru Schedule"
                subtitle="Automatically includes jobs assigned to you."
                feed={state.personal}
                headers={headers}
                onChanged={load}
              />
              {state.can_manage_company && (
                <SubscriptionCard
                  scopeKey="company"
                  title="Company Schedule"
                  subtitle="View the full Gleam Guru operational schedule."
                  badge="Management Access"
                  feed={state.company}
                  headers={headers}
                  onChanged={load}
                />
              )}

              <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-[11px] text-zinc-600 leading-relaxed" data-testid="calendar-sync-note">
                Gleam Guru remains the source of truth. External calendars are synchronized schedule views —
                changes to jobs, times, or assignments update your calendar automatically.
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarSyncDialog;
